/**
 * useBusinessHours — single source of truth for salon hours across the app.
 *
 * Fetches `/api/settings/business-hours` once, caches in module scope so
 * every component using the hook hits the network at most once per page
 * load. Returns:
 *   • `loading`        — true while the first fetch is in flight
 *   • `shortLine`      — "Mon–Fri 9:30am–7:30pm · Sat 9:30am–7pm · Sun 11am–5:30pm"
 *   • `byDayLines`     — ["Sun 11am–5:30pm", "Mon 9:30am–7:30pm", ...]
 *   • `rows`           — full per-day records with open/close in 24h + display
 *   • `schemaOrg`      — "Su 11:00-17:30, Mo-Fr 09:30-19:30, Sa 09:30-19:00"
 *   • `todayDisplay`   — "9:30am–7:30pm" for the current weekday, or null if closed
 *
 * All hours strings come from the backend so any owner override (via
 * Operating Mode → Hours) propagates everywhere automatically.
 */
import { useEffect, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

let _cache = null;
let _inflight = null;
const _subscribers = new Set();

const DAY_INDEX = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

async function _fetch() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      // Fetch hours + today's override in parallel — one combined cache
      // entry so every consumer gets a unified picture of "what's today".
      const [hoursRes, overrideRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/settings/business-hours`).then((r) => r.ok ? r.json() : null),
        fetch(`${BACKEND_URL}/api/settings/calendar-overrides/public/today`).then((r) => r.ok ? r.json() : null),
      ]);
      _cache = {
        ...(hoursRes || {
          short_line: "Mon–Fri 9:30am–7:30pm · Sat 9:30am–7pm · Sun 11am–5:30pm",
          by_day_lines: ["Sun 11am–5:30pm", "Mon–Fri 9:30am–7:30pm", "Sat 9:30am–7pm"],
          rows: [],
          schema_org: "Mo-Fr 09:30-19:30, Sa 09:30-19:00, Su 11:00-17:30",
          weekly: {},
          source: "fallback",
        }),
        override: overrideRes?.override || null,
        next_open: overrideRes?.next_open || null,
      };
    } catch {
      _cache = {
        short_line: "Mon–Fri 9:30am–7:30pm · Sat 9:30am–7pm · Sun 11am–5:30pm",
        by_day_lines: ["Sun 11am–5:30pm", "Mon–Fri 9:30am–7:30pm", "Sat 9:30am–7pm"],
        rows: [],
        schema_org: "Mo-Fr 09:30-19:30, Sa 09:30-19:00, Su 11:00-17:30",
        weekly: {},
        source: "fallback",
        override: null,
        next_open: null,
      };
    } finally {
      _inflight = null;
    }
    return _cache;
  })();
  return _inflight;
}

/** Bust the cache (use after the owner saves new hours). */
export function refreshBusinessHours() {
  _cache = null;
  _inflight = null;
  _subscribers.forEach((cb) => cb(null));
  // Re-fetch in the background; subscribers will be notified again.
  _fetch().then((data) => _subscribers.forEach((cb) => cb(data)));
}

export function useBusinessHours() {
  const [data, setData] = useState(_cache);

  useEffect(() => {
    let alive = true;
    if (!_cache) {
      _fetch().then((d) => { if (alive) setData(d); });
    }
    const cb = (d) => { if (alive) setData(d); };
    _subscribers.add(cb);
    return () => {
      alive = false;
      _subscribers.delete(cb);
    };
  }, []);

  if (!data) {
    return {
      loading: true,
      shortLine: "",
      byDayLines: [],
      rows: [],
      schemaOrg: "",
      todayDisplay: null,
      todayClosed: false,
    };
  }

  // Today's override (if any). When `type=closed`, the salon is closed today
  // even if the weekly schedule says otherwise. When `type=special_hours`,
  // we override the displayed open/close pair for today.
  const override = data.override || null;
  const overrideClosed = override?.type === "closed";
  const overrideOpen = override?.open || null;
  const overrideClose = override?.close || null;

  // Compute "today" display in the salon's local Central time so it's
  // accurate for owners and customers regardless of their browser TZ.
  const todayCT = new Date().toLocaleString("en-US", { timeZone: "America/Chicago", weekday: "long" })
    .toLowerCase();
  const todayCfg = (data.weekly || {})[todayCT];
  const todayRow = (data.rows || []).find((r) => r.day === todayCT);

  let todayDisplay = todayRow ? `${todayRow.open_display}–${todayRow.close_display}` : null;
  let todayClosed = !todayCfg;
  if (overrideClosed) {
    todayDisplay = null;
    todayClosed = true;
  } else if (overrideOpen && overrideClose) {
    const fmt = (hhmm) => {
      const [h, m] = hhmm.split(":").map((n) => parseInt(n));
      const suf = h < 12 ? "am" : "pm";
      const h12 = h % 12 || 12;
      return `${h12}:${String(m).padStart(2, "0")}${suf}`.replace(":00", "");
    };
    todayDisplay = `${fmt(overrideOpen)}–${fmt(overrideClose)}`;
    todayClosed = false;
  }

  return {
    loading: false,
    shortLine: data.short_line || "",
    byDayLines: data.by_day_lines || [],
    rows: data.rows || [],
    schemaOrg: data.schema_org || "",
    weekly: data.weekly || {},
    source: data.source || "default",
    todayDisplay,
    todayClosed,
    todayDayName: todayCT,
    todayDayIndex: DAY_INDEX.indexOf(todayCT),
    override,
    nextOpen: data.next_open || null,
  };
}
